import api, { baseApi } from './api';
import {
  Action,
  User,
  Product,
  Price,
  Field,
  Content,
  ContentTypeField,
  ContentTypeFieldView,
} from '@/lib/types';

export async function fetchActions(
  workspaceId: string,
  actionType: string,
  startDate: string,
  endDate: string,
) {
  const response = await api.get<{ resource: Action[] }>(
    `workspace/${workspaceId}/actions/${actionType}/${startDate}/${endDate}`,
  );
  return response.data.resource ?? [];
}

export async function usersLoader({ params }: { params: any }) {
  const response = await api.get(`workspace/${params.workspaceId}/users`);
  return response.data.resource as User[];
}

export async function fieldLoader({ params }: { params: any }) {
  const response = await api.get(`fields/${params.id}`);
  return response.data.data as Field;
}

export async function fieldsLoader({ params }: { params: any }) {
  const response = await api.get(
    `fields/workspace/${params.workspaceId}?ignore_section_field_type=true`,
  );
  return response.data.data as Field[];
}

export async function userLoader({ params }: { params: any }) {
  const response = await api.get(`workspace/${params.workspaceId}/users/${params.userId}`);
  return response.data.resource as User;
}

export async function userProfileLoader() {
  const response = await api.get(`users/me`);
  return response.data.resource as User;
}

export async function contentFieldLoader({ params }: { params: any }) {
  const response = await api.get(
    `content-type-fields/${params.contentTypeId}/${params.workspaceId}/data`,
  );
  return response.data.data as ContentTypeField[];
}

export async function contentLoader({ params }: { params: any }) {
  const response = await baseApi.get(`public/content/${params.referenceId}`);
  return response.data.data as {
    content: Content;
    subContents: any;
    contentTypeFields: ContentTypeFieldView[];
    workspaceCreatorEmail: string;
    workspaceName: string;
  };
}

export async function sportTypesLoader() {
  const response = await baseApi.get(`/public/sport-types`);
  return response.data.resource;
}

interface Role {
  count: number;
  role_name: string;
  role_id: number;
  stripe_price_id: string;
}

export async function productsLoader({ params }: { params: any }) {
  const [productRes, priceRes, userCountRes] = await Promise.all([
    api.get(`workspace/products`),
    api.get(`workspace/prices`),
    api.get(`workspace/${params.workspaceId}/roles`),
  ]);

  const products: Product[] = productRes.data.data;
  const prices: Price[] = priceRes.data.data;
  const userRoleCounts: Role[] = userCountRes.data.data;

  const roleCountMap = userRoleCounts.reduce((map, item) => {
    map.set(item.stripe_price_id, (map.get(item.stripe_price_id) || 0) + item.count);
    return map;
  }, new Map<string, number>());

  const dataPrices = new Map<string, Price>();
  prices.forEach((price) => {
    price.usage_count = roleCountMap.get(price.id) || 0;
    dataPrices.set(price.id, price);
  });

  products.forEach((product) => {
    product.price = dataPrices.get(product?.default_price?.id) || null;
  });

  return products;
}
